import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { mediaUrl } from "../lib/api";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

// Fallback backdrop for posts without cover
const FALLBACK =
  "https://images.unsplash.com/photo-1544991337-95176b5233c4?crop=entropy&cs=srgb&fm=jpg&q=80&w=2400";

export default function HeroCarousel({ posts = [] }) {
  const [idx, setIdx] = useState(0);
  const slides = posts.slice(0, 5);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6500);
    return () => clearInterval(t);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const current = slides[idx];
  const cover = mediaUrl(current.cover_image_id, "desktop") || current.cover_url || FALLBACK;

  const go = (delta) => setIdx((i) => (i + delta + slides.length) % slides.length);

  return (
    <section
      className="relative bg-orion-purple text-white overflow-hidden"
      data-testid="hero-carousel"
      aria-roledescription="carousel"
    >
      {/* Backdrop layers */}
      {slides.map((s, i) => {
        const bg = mediaUrl(s.cover_image_id, "desktop") || s.cover_url || FALLBACK;
        return (
          <div
            key={s.id}
            aria-hidden={i !== idx}
            className={`absolute inset-0 bg-center bg-cover transition-opacity duration-[1200ms] ease-out ${i === idx ? "opacity-100" : "opacity-0"}`}
            style={{ backgroundImage: `url(${bg})` }}
          />
        );
      })}
      <div className="absolute inset-0 bg-gradient-to-b from-[#2C1139]/60 via-[#2C1139]/45 to-[#2C1139]/95" />
      <div className="absolute inset-0 grain opacity-20 pointer-events-none" />

      {/* Content */}
      <div className="container-orion relative py-10 md:py-24 lg:py-32 grid md:grid-cols-12 gap-6 items-end min-h-[52vh] md:min-h-[68vh]">
        <div className="md:col-span-9 lg:col-span-8">
          <Link
            to={`/categoria/${(current.categories?.[0] || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            className="cat-chip !text-orion-gold mb-3 md:mb-6 block hover:!text-orion-orange transition-colors"
            data-testid="hero-category"
          >
            {current.categories?.[0] || "Edição Orion"}
          </Link>

          <h1
            className="font-serif font-semibold leading-[1.02] tracking-tight text-white text-[1.75rem] sm:text-4xl md:text-5xl lg:text-[4.5rem]"
            data-testid="hero-title"
          >
            <Link to={`/artigo/${current.slug}`} className="hover:text-orion-orange transition-colors">
              {current.title}
            </Link>
          </h1>

          {current.excerpt && (
            <p className="mt-3 md:mt-6 text-sm md:text-lg text-white/85 max-w-2xl leading-relaxed line-clamp-2 md:line-clamp-3">
              {current.excerpt}
            </p>
          )}

          <div className="mt-5 md:mt-8 flex items-center gap-4">
            <Link
              to={`/artigo/${current.slug}`}
              className="inline-flex items-center gap-2 bg-orion-orange text-white px-5 md:px-6 py-2.5 md:py-3 text-[11px] md:text-xs uppercase tracking-widest hover:bg-[#E04828] transition-colors"
              data-testid="hero-cta"
            >
              Ler artigo <ArrowRight size={14} />
            </Link>
            <span className="text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-white/60 hidden sm:inline">
              {String(idx + 1).padStart(2, "0")} <span className="text-orion-gold">/</span> {String(slides.length).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* Nav arrows */}
        {slides.length > 1 && (
          <div className="md:col-span-3 lg:col-span-4 flex md:justify-end items-end gap-2">
            <button
              onClick={() => go(-1)}
              aria-label="Anterior"
              data-testid="hero-prev"
              className="p-3 border border-white/25 hover:border-orion-orange hover:text-orion-orange transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Próximo"
              data-testid="hero-next"
              className="p-3 border border-white/25 hover:border-orion-orange hover:text-orion-orange transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Progress dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              className={`h-[2px] transition-all ${i === idx ? "w-10 bg-orion-orange" : "w-5 bg-white/40 hover:bg-white/70"}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
